import {
  Delete,
  Edit,
  Star,
  StarBorder,
  Visibility,
  VisibilityOff,
} from '@material-ui/icons';
import strings from 'common/strings';
import Input from 'components/shared/Inputs/TextInput';
import Modal from 'components/shared/Modal';
import PortalAlert from 'components/shared/PortalAlert';
import { useWindowSize } from 'hooks';
import { VelocityEngageFolioFolderDocument } from 'models';
import React, { useState } from 'react';
import { fetchMedia, extractBlob } from 'api/engage/fetchMedia';
import EngageMediaPlaceholder from '../EngageMediaPlaceholder';
import './EngageMediaItem.scss';
import { useShowCarfaxModal } from '../CarfaxModalProvider';

/**
 *  Simple component that uses an anchor when it is required to open in a new tab, otherwise using fetch.
 */
const AnchorOrDiv = ({
  children,
  media,
  isOpenNewTab,
  isDragging,
  onMediaClick,
}: React.PropsWithChildren<{
  media: VelocityEngageFolioFolderDocument;
  isOpenNewTab: boolean;
  isDragging: boolean;
  onMediaClick: (mediaUrl: string) => Promise<void>;
}>) => {
  return isOpenNewTab ? (
    <a href={media.url} target="_blank" rel="noreferrer">
      {children}
    </a>
  ) : (
    <div
      role="none"
      onClick={() => {
        if (!isDragging) {
          onMediaClick(media.url);
        }
      }}
    >
      {children}
    </div>
  );
};

interface EngageMediaItemComponentProps {
  inFeaturedMediaContainer: boolean;
  onStar: (document: VelocityEngageFolioFolderDocument) => void;
  onPublish: (document: VelocityEngageFolioFolderDocument) => void;
  isDragging: boolean;
  folderId: string;
  vehicleId: string;
  media: VelocityEngageFolioFolderDocument;
  onMediaClick: (mediaUrl: string) => Promise<void>;
  isEditModalOpen: boolean;
  setIsEditModalOpen: (isEditModalOpen: boolean) => void;
  isDeleteModalOpen: boolean;
  setIsDeleteModalOpen: (isEditModalOpen: boolean) => void;
  isOpenNewTab: boolean;
}

const EngageMediaItemOverlay: React.FC<EngageMediaItemComponentProps> = ({
  inFeaturedMediaContainer = false,
  onStar,
  onPublish,
  isDragging,
  folderId,
  vehicleId,
  media,
  onMediaClick,
  isEditModalOpen,
  setIsEditModalOpen,
  isDeleteModalOpen,
  setIsDeleteModalOpen,
  isOpenNewTab,
}) => {
  const { featured: isFeatured, visible: isVisible } = media;
  const StarIcon = isFeatured ? Star : StarBorder;
  const starIconClassName = isFeatured ? 'EngageMediaItem-star-icon' : '';

  const EyeIcon = isVisible ? Visibility : VisibilityOff;
  const eyeIconClassName = isVisible ? '' : 'EngageMediaItem-eye-icon';

  /**
   * Inner element that is put inside a div or an anchor.
   */
  const EngageMediaInnerComponent: React.FC<{
    media: VelocityEngageFolioFolderDocument;
    folderId: string;
    vehicleId: string;
  }> = ({ media, folderId, vehicleId }) => {
    return media.url ? (
      <>
        <img
          className="EngageMediaItem-info-image"
          src={media.previewUrl}
          alt="car"
        />
        <div className="EngageMediaItem-info-image-overlay" />
        <span className="EngageMediaItem-info-title">{media.displayLabel}</span>
      </>
    ) : (
      <EngageMediaPlaceholder
        title={media.displayLabel}
        folderId={folderId}
        vehicleId={vehicleId}
      />
    );
  };

  return (
    <div className="EngageMediaItem item">
      <div className="item-content">
        <div>
          <div className="EngageMediaItem-info" role="none">
            <AnchorOrDiv
              media={media}
              isOpenNewTab={isOpenNewTab}
              isDragging={isDragging}
              onMediaClick={onMediaClick}
            >
              <EngageMediaInnerComponent
                media={media}
                folderId={folderId}
                vehicleId={vehicleId}
              />
            </AnchorOrDiv>
          </div>
        </div>
        <div className="EngageMediaItem-icons">
          <StarIcon
            className={`EngageMediaItem-icon ${starIconClassName}`}
            onClick={() => onStar(media)}
          />
          {!inFeaturedMediaContainer && (
            <>
              <EyeIcon
                className={`EngageMediaItem-icon ${eyeIconClassName}`}
                onClick={() => onPublish(media)}
              />
              {media.canDelete && (
                <>
                  <Delete
                    className="EngageMediaItem-icon"
                    onClick={() => {
                      if (isEditModalOpen) {
                        setIsEditModalOpen(false);
                      }
                      setIsDeleteModalOpen(!isDeleteModalOpen);
                    }}
                  />
                  <Edit
                    className="EngageMediaItem-icon"
                    onClick={() => {
                      if (isDeleteModalOpen) {
                        setIsDeleteModalOpen(false);
                      }
                      setIsEditModalOpen(!isEditModalOpen);
                    }}
                  />
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const EngageMediaItemHorizontal: React.FC<EngageMediaItemComponentProps> = ({
  inFeaturedMediaContainer = false,
  onStar,
  onPublish,
  isDragging,
  folderId,
  vehicleId,
  media,
  onMediaClick,
  isEditModalOpen,
  setIsEditModalOpen,
  isDeleteModalOpen,
  setIsDeleteModalOpen,
  isOpenNewTab,
}) => {
  const { featured, visible, displayLabel } = media;
  const StarIcon = featured ? Star : StarBorder;
  const starIconClassName = featured
    ? 'EngageMediaItemHorizontal-star-icon'
    : '';

  const EyeIcon = visible ? Visibility : VisibilityOff;
  const eyeIconClassName = visible ? '' : 'EngageMediaItemHorizontal-eye-icon';

  /**
   * Inner element that is put inside a div or an anchor.
   */
  const EngageMediaInnerComponent: React.FC<{
    media: VelocityEngageFolioFolderDocument;
    folderId: string;
    vehicleId: string;
  }> = ({ media, folderId, vehicleId }) => {
    return media.url ? (
      <img
        className="EngageMediaItemHorizontal-image"
        src={media.previewUrl}
        alt="car"
      />
    ) : (
      <EngageMediaPlaceholder
        title={media.displayLabel}
        folderId={folderId}
        vehicleId={vehicleId}
      />
    );
  };

  return (
    <div className="item EngageMediaItemHorizontal-margin">
      <div className="item-content">
        <div className="EngageMediaItemHorizontal">
          <AnchorOrDiv
            media={media}
            isOpenNewTab={isOpenNewTab}
            isDragging={isDragging}
            onMediaClick={onMediaClick}
          >
            <EngageMediaInnerComponent
              media={media}
              folderId={folderId}
              vehicleId={vehicleId}
            />
          </AnchorOrDiv>
          <div
            className={`EngageMediaItemHorizontal-info${
              media.url ? '' : ' EngageMediaItemHorizontal-info-padding'
            }`}
          >
            <span className="EngageMediaItemHorizontal-title">
              {displayLabel}
            </span>
            <div className="EngageMediaItemHorizontal-icons">
              <StarIcon
                className={`EngageMediaItemHorizontal-icon ${starIconClassName}`}
                onClick={() => onStar(media)}
              />
              {!inFeaturedMediaContainer && (
                <>
                  <EyeIcon
                    className={`EngageMediaItemHorizontal-icon ${eyeIconClassName}`}
                    onClick={() => onPublish(media)}
                  />
                  {media.canDelete && (
                    <>
                      <Delete
                        className="EngageMediaItemHorizontal-icon"
                        onClick={() => {
                          if (isEditModalOpen) {
                            setIsEditModalOpen(false);
                          }
                          setIsDeleteModalOpen(!isDeleteModalOpen);
                        }}
                      />
                      <Edit
                        className="EngageMediaItemHorizontal-icon"
                        onClick={() => {
                          if (isDeleteModalOpen) {
                            setIsDeleteModalOpen(false);
                          }
                          setIsEditModalOpen(!isEditModalOpen);
                        }}
                      />
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface EngageMediaItemProps {
  media: VelocityEngageFolioFolderDocument;
  inFeaturedMediaContainer: boolean;
  onStar: (document: VelocityEngageFolioFolderDocument) => void;
  onPublish: (document: VelocityEngageFolioFolderDocument) => void;
  onDelete: (document: VelocityEngageFolioFolderDocument) => void;
  onEdit: (document: VelocityEngageFolioFolderDocument, title: string) => void;
  isDragging: boolean;
  folderId: string;
  vehicleId: string;
  isOpenNewTab: boolean;
}

const EngageMediaItem: React.FC<EngageMediaItemProps> = ({
  media,
  inFeaturedMediaContainer,
  onStar,
  onEdit,
  onPublish,
  onDelete,
  isDragging,
  folderId,
  vehicleId,
  isOpenNewTab,
}) => {
  const windowSize = useWindowSize();
  const [info, setInfo] = useState('');
  const [error, setError] = useState('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [ephemeralMediaTitle, setEphemeralMediaTitle] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const showCarfaxModal = useShowCarfaxModal();

  const onMediaClick = async (mediaUrl: string) => {
    if (mediaUrl) {
      setInfo(strings.ENGAGE_MEDIA_FETCHING);
      await fetchMedia(mediaUrl, (value: string) => {
        setInfo(value);
        return value;
      }).then(async (res) => {
        if (res.status === 200) {
          setInfo('');
          if (media.type.name === 'CARFAX') {
            showCarfaxModal(await res.json());
          } else {
            extractBlob(res);
          }
        } else {
          setError(strings.ENGAGE_MEDIA_FETCH_FAILED);
        }
      });
    } else {
      setError(strings.NO_DOCUMENT_URL);
    }
  };

  return (
    <>
      {windowSize.isMobileViewport() ? (
        <EngageMediaItemHorizontal
          inFeaturedMediaContainer={inFeaturedMediaContainer}
          onStar={onStar}
          onPublish={onPublish}
          isDragging={isDragging}
          folderId={folderId}
          vehicleId={vehicleId}
          media={media}
          onMediaClick={onMediaClick}
          isEditModalOpen={isEditModalOpen}
          setIsEditModalOpen={setIsEditModalOpen}
          isDeleteModalOpen={isDeleteModalOpen}
          setIsDeleteModalOpen={setIsDeleteModalOpen}
          isOpenNewTab={isOpenNewTab}
        />
      ) : (
        <EngageMediaItemOverlay
          inFeaturedMediaContainer={inFeaturedMediaContainer}
          onStar={onStar}
          onPublish={onPublish}
          isDragging={isDragging}
          folderId={folderId}
          vehicleId={vehicleId}
          media={media}
          onMediaClick={onMediaClick}
          isEditModalOpen={isEditModalOpen}
          setIsEditModalOpen={setIsEditModalOpen}
          isDeleteModalOpen={isDeleteModalOpen}
          setIsDeleteModalOpen={setIsDeleteModalOpen}
          isOpenNewTab={isOpenNewTab}
        />
      )}
      <PortalAlert
        open={!!error}
        handleClose={() => setError('')}
        message={error}
        variant="error"
      />
      <PortalAlert
        open={!!info}
        handleClose={() => setInfo('')}
        message={info}
        variant="warning"
      />
      <Modal
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onPrimaryButtonClick={async () => {
          if (onEdit) {
            setIsUpdating(true);
            await onEdit(media, ephemeralMediaTitle);
            setIsUpdating(false);
            setIsEditModalOpen(false);
          }
        }}
        title={strings.CHANGE_MEDIA_NAME}
        primaryButtonLabel={strings.SAVE}
        secondaryButtonLabel={strings.CANCEL}
        primaryButtonVariant="danger"
        className="EngageMediaItem-edit-modal"
        primaryButtonLoading={isUpdating}
      >
        <Input
          value={ephemeralMediaTitle}
          onChange={(event) => setEphemeralMediaTitle(event.target.value)}
        />
      </Modal>
      <Modal
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onPrimaryButtonClick={async () => {
          if (onDelete) {
            setIsDeleting(true);
            await onDelete(media);
            setIsDeleting(false);
            setIsDeleteModalOpen(false);
          }
        }}
        title={strings.ARE_YOU_SURE}
        primaryButtonLabel={strings.DELETE}
        secondaryButtonLabel={strings.CANCEL}
        primaryButtonVariant="danger"
        className="EngageMediaItem-delete-modal"
        primaryButtonLoading={isDeleting}
      >
        <div />
      </Modal>
    </>
  );
};

export default EngageMediaItem;
